//  pattern taken from: https://kentcdodds.com/blog/application-state-management-with-react
//  returns an HOC wrapper that allows usage of useErrorManager() hook in functional components
//  to grab the errors array and helper methods. allows separation of concerns of the errors object
//  away from local state management and prevents prop-drilling of the errors object+helper methods

import React, { useContext, createContext, useMemo, useReducer } from 'react';
import { modelToValidatorAction } from './modelToValidatorAction';
import { ERRORS_OBJ } from './errorsObj';

const ErrorManagerContext = createContext();

function errorsReducer(state, action) {
  switch (action.type) {
    case 'ADD_ERRORS': {
      const uniqueErrors = [...new Set([...state.errors, ...action.payload])];
      return { errors: uniqueErrors };
    }
    case 'REMOVE_ERROR': {
      // removes the error via action.payload from the errors array
      const filtered = state.errors.filter(error => error !== action.payload);
      return { errors: [...filtered] };
    }
    default:
      throw new Error(`Unsupported action type: ${action.type}`);
  }
}

function ErrorManagerProvider(props) {
  const [state, dispatch] = useReducer(errorsReducer, { errors: [] });

  const value = useMemo(() => [state, dispatch], [state]);
  return <ErrorManagerContext.Provider value={value} {...props} />;
}

function useErrorManager() {
  const context = useContext(ErrorManagerContext);
  if (!context) throw new Error(`useErrorManager must be used within a ErrorManagerProvider`);
  const [state, dispatch] = context;

  function addErrors(arrayErrorString) {
    // use this method if you want to addErrors _immediately_, before the validation onSave occurs
    //  e.g. when we update a timed-entry listing's location to somewhere that changes the listing's
    //  timezone, we want to prevent that by adding an error immediately upon comparing the two tz's
    //  instead of validating on-save
    if (!Array.isArray(arrayErrorString)) throw new Error(`Argument provided to addErrors() must be an Array.`);
    dispatch({ type: 'ADD_ERRORS', payload: arrayErrorString });
  }

  function removeErrors(arrayErrorStrings) {
    // accepts array of error strings to remove input from errors array
    if (!Array.isArray(arrayErrorStrings)) throw new Error(`Argument provided to removeErrors() must be an Array.`);
    arrayErrorStrings.forEach(errorString => dispatch({ type: 'REMOVE_ERROR', payload: errorString }));
  }

  function containsErrors(errorsArray) {
    // accepts string, returns boolean if the errors array contains string
    if (!Array.isArray(errorsArray)) throw new Error(`Argument provided to containsErrors() must be an Array.`);
    return errorsArray.some(error => state.errors.includes(error));
  }

  function containsAnyErrors(data) {
    // currently accepts a very particular data object that looks like { event: { ... } }
    // returns boolean if there are _any_ errors in the errors array

    if (!data.event) throw new Error(`Argument passed to containsAnyErrors() does not have an \`event\` property`);
    const { event } = data;
    const newErrors = [];

    Object.entries(event).forEach(([key, value]) => {
      switch (key) {
        default: {
          // by default, lookup each `key` and run its stored method, which returns an array of error strings
          if (modelToValidatorAction[key]){
            newErrors.push(...modelToValidatorAction[key](value));
          }
          break;
        }
        case 'rates': {
          if (!event.srcCurrency) throw new Error(`event.srcCurrency is blank`);
          newErrors.push(...modelToValidatorAction.rates(
            value,
            event.srcCurrency,
            event.capacity,
            event.urgencyIndicatorEnabled
          ));
          break;
        }
        case 'address': {
          // special case: we need to also pass in the country and whitelistedCountries for a validation check
          if (!event.country) throw new Error(`event.country is blank`);
          if (!data.whitelistedCountries) throw new Error(`data.whitelistedCountries is blank`);

          newErrors.push(...modelToValidatorAction.address(value, event.country, data.whitelistedCountries));
          break;
        }
        case 'virtualInfo': {
          newErrors.push(...modelToValidatorAction.virtualInfo(value, event.virtual));
          break;
        }
      }
    });

    const uniqueErrors = [...new Set(newErrors)];
    addErrors(uniqueErrors);
    return uniqueErrors.length > 0;
  }

  return {
    errors: state.errors, // for debugging purposes, to see the array of errors
    ERRORS_OBJ, // use this object to reference any error strings (e.g. `ERRORS_OBJ.rates.NO_TITLE(rate.key)`)
    addErrors, // use this only if unable to utilize onSave validation - used to _immediately_ add an error elsewhere
    removeErrors, // accepts array of error strings to remove strings from errors array
    containsErrors, // accepts array of error strings, returns bool of errors array containing any of the strings passed
    containsAnyErrors, // returns boolean if there are _any_ errors in the errors array
  };
}

const withErrorManagerHOC = Component => {
  // higher order component, to allow using hooks within a class component (i.e. create form)
  // https://infinum.com/the-capsized-eight/how-to-use-react-hooks-in-class-components

  return props => {
    const { errors, removeErrors, containsErrors, containsAnyErrors } = useErrorManager();
    const errorManager = {
      errors,
      ERRORS_OBJ,
      removeErrors,
      containsErrors,
      containsAnyErrors,
    };

    return <Component errorManager={errorManager} {...props} />;
  };
};

export {
  ErrorManagerContext, // context object itself, in case you need to use it directly
  ErrorManagerProvider, // HOC wrapper to enable using useErrorManager() throughout its children
  useErrorManager, // hook that can be called anywhere in functional components to grab errors array + methods
  withErrorManagerHOC, // HOC function that enables the same functionality as useErrorManager _without_ a hook
};
